import React, { useRef } from "react";
import { Tooltip, TooltipProps } from "@mui/material";
import { mixpanelEventHandler } from "../../utilities/reactMixpanelHandler";
import { CLAIMS_MIXPANEL_EVENTS } from "./claimsMixpanelEvents";

/**
 * Get contextual help text based on claim status
 * This provides guidance to facility managers on what action is needed
 */
export function getStatusHelpText(status: string): string | null {
  const statusLower = status?.toLowerCase() || "";

  if (statusLower.includes("settled")) {
    return "This claim has been settled, please direct the tenant to the tenant portal if they have questions regarding the settlement";
  }

  if (statusLower.includes("facility action")) {
    return "Please check your email and search for the claim number to complete the requested action";
  }

  if (statusLower.includes("tenant action")) {
    return "The tenant has outstanding actions, please direct them to the tenant portal address tenant.safelease.com in order to complete the outstanding actions";
  }

  if (statusLower.includes("needs review") || statusLower.includes("needs approval") || statusLower.includes("pending settlement")) {
    return "The adjusters are reviewing this claim and will communicate any needs via the tenant portal at tenant.safelease.com";
  }

  if (statusLower.includes("adjuster review")) {
    return "The adjusters are reviewing this claim and will communicate any needs via the tenant portal at tenant.safelease.com";
  }

  // No help text for other statuses
  return null;
}

interface StatusHelpTooltipProps {
  status: string;
  children: React.ReactElement;
  placement?: TooltipProps["placement"];
  claimId?: string;
}

/**
 * Tooltip component that shows contextual help text for claim statuses
 * Only shows tooltip if there's relevant help text for the status
 */
export function StatusHelpTooltip({ status, children, placement = "top", claimId }: StatusHelpTooltipProps) {
  const helpText = getStatusHelpText(status);
  const hasTrackedOpen = useRef(false);

  // If no help text, render children without tooltip
  if (!helpText) {
    return children;
  }

  const handleTooltipOpen = () => {
    // Track tooltip viewed (only once per tooltip instance)
    if (!hasTrackedOpen.current) {
      mixpanelEventHandler(CLAIMS_MIXPANEL_EVENTS.drawer.statusTooltipViewed, {
        claim_id: claimId || null,
        claim_status: status,
      });
      hasTrackedOpen.current = true;
    }
  };

  return (
    <Tooltip
      title={helpText}
      arrow
      placement={placement}
      enterDelay={300}
      leaveDelay={200}
      onOpen={handleTooltipOpen}
      sx={{
        "& .MuiTooltip-tooltip": {
          maxWidth: 400,
          fontSize: "0.875rem",
          padding: "12px 16px",
          backgroundColor: "rgba(0, 0, 0, 0.9)",
          lineHeight: 1.5,
        },
        "& .MuiTooltip-arrow": {
          color: "rgba(0, 0, 0, 0.9)",
        },
      }}
    >
      {children}
    </Tooltip>
  );
}

export default StatusHelpTooltip;
